import type { MonthlyTrackingRow } from '@/api/planning/monthly-tracking';
import type { MonthlyTrackingStatusFilter } from '@/stores/monthly-tracking';

export interface GroupedPatientRow {
  patientId: number;
  patientFullName: string | null;
  cap: number;
  planned: number;
  actual: number;
  percentUsed: number;
  driftDays: string[];
  driftCount: number;
  careerCount: number;
  children: MonthlyTrackingRow[];
}

export function applySearch(
  input: MonthlyTrackingRow[], term: string | null,
): MonthlyTrackingRow[] {
  const cleaned = (term ?? '').trim().toLowerCase();
  if (!cleaned) return input;
  return input.filter((row) => (
    (row.patientFullName || '').toLowerCase().includes(cleaned)
  ));
}

export function rowMatchesStatus(
  row: MonthlyTrackingRow, status: MonthlyTrackingStatusFilter,
): boolean {
  switch (status) {
  case 'drift': return row.driftDays.length > 0;
  case 'unmarked': return row.planned > row.actual;
  case 'over_budget': return row.actual > row.cap;
  default: return true;
  }
}

export function applyStatus(
  input: MonthlyTrackingRow[], status: MonthlyTrackingStatusFilter,
): MonthlyTrackingRow[] {
  if (status === 'all') return input;
  return input.filter((row) => rowMatchesStatus(row, status));
}

export function applyCareer(
  input: MonthlyTrackingRow[], careerIn: string[],
): MonthlyTrackingRow[] {
  if (careerIn.length === 0) return input;
  return input.filter((row) => careerIn.includes(row.career));
}

function aggregate(children: MonthlyTrackingRow[]): GroupedPatientRow {
  const cap = children.reduce((sum, c) => sum + c.cap, 0);
  const planned = children.reduce((sum, c) => sum + c.planned, 0);
  const actual = children.reduce((sum, c) => sum + c.actual, 0);
  const driftDays = Array.from(new Set(children.flatMap((c) => c.driftDays))).sort();
  const first = children[0];
  return {
    patientId: first?.patientId ?? 0,
    patientFullName: first?.patientFullName ?? null,
    cap,
    planned,
    actual,
    percentUsed: cap > 0 ? actual / cap : 0,
    driftDays,
    driftCount: driftDays.length,
    careerCount: children.length,
    children,
  };
}

export function groupByPatient(input: MonthlyTrackingRow[]): GroupedPatientRow[] {
  const byPatient = new Map<number, MonthlyTrackingRow[]>();
  for (const row of input) {
    const list = byPatient.get(row.patientId) ?? [];
    list.push(row);
    byPatient.set(row.patientId, list);
  }
  return Array.from(byPatient.values()).map(aggregate);
}

// Where the patient's name in the tracking leads: their profile, on the
// requirements the month is measured against.
export function patientRequirementsPath(patientId: number): string {
  return `/patients/${patientId}?tab=requirements`;
}
